/**
 * Camera: focus cell + local view transform. Unbounded pan/zoom/rotation
 * with float64-exact composition (see cell.ts).
 *
 * The view transform within the focus cell is:
 *   screen = translate(cx, cy) ∘ rotate(rotation) ∘ scale(scale) ∘ translate(-x, -y)
 * where (x, y) is the focus point (world position at screen center) in the
 * focus cell's local space, and `scale` is screen px per focus-cell unit.
 *
 * `scale` is kept in [0.5, 2) by descending into child cells while zooming
 * in and ascending while zooming out, so the zoom range is 2^MAX_DEPTH.
 */

import { CellTree, MAX_DEPTH } from "./cell";
import { Mat2D, apply, inv, mul, rotateM, scaleM, translate, Vec2 } from "./math";

export interface CameraState {
  focusCellId: string;
  x: number;
  y: number;
  scale: number;
  rotation: number;
}

export const MIN_LOCAL_SCALE = 0.5;
export const MAX_LOCAL_SCALE = 2;

export class Camera {
  readonly tree: CellTree;
  focusCellId = "";
  x = 0;
  y = 0;
  scale = 100; // px per root unit (screen-ish default)
  rotation = 0;

  constructor(tree: CellTree, state?: Partial<CameraState>) {
    this.tree = tree;
    if (state) {
      if (state.focusCellId !== undefined) this.focusCellId = state.focusCellId;
      if (state.x !== undefined) this.x = state.x;
      if (state.y !== undefined) this.y = state.y;
      if (state.scale !== undefined) this.scale = state.scale;
      if (state.rotation !== undefined) this.rotation = state.rotation;
    }
  }

  get state(): CameraState {
    return {
      focusCellId: this.focusCellId,
      x: this.x,
      y: this.y,
      scale: this.scale,
      rotation: this.rotation,
    };
  }

  get depth(): number {
    return this.focusCellId.length;
  }

  /** Screen (css px) -> focus-cell local transform. */
  viewTransform(cx: number, cy: number): Mat2D {
    return mul(
      translate(cx, cy),
      mul(rotateM(this.rotation), mul(scaleM(this.scale), translate(-this.x, -this.y))),
    );
  }

  screenToFocus(cx: number, cy: number, p: Vec2): Vec2 {
    return apply(inv(this.viewTransform(cx, cy)), p);
  }

  /** Focus-cell local point -> screen. */
  focusToScreen(cx: number, cy: number, p: Vec2): Vec2 {
    return apply(this.viewTransform(cx, cy), p);
  }

  /** Transform from arbitrary cell local coords to focus-cell local coords. */
  cellToFocus(cellId: string): Mat2D {
    return this.tree.transform(cellId, this.focusCellId);
  }

  /** Transform from focus-cell local coords to arbitrary cell local coords. */
  focusToCell(cellId: string): Mat2D {
    return this.tree.transform(this.focusCellId, cellId);
  }

  /** Full transform: cell local -> screen. */
  cellToScreen(cx: number, cy: number, cellId: string): Mat2D {
    return mul(this.viewTransform(cx, cy), this.cellToFocus(cellId));
  }

  /** Full transform: screen -> cell local. */
  screenToCell(cx: number, cy: number, cellId: string): Mat2D {
    return mul(inv(this.cellToFocus(cellId)), inv(this.viewTransform(cx, cy)));
  }

  /** Convert a screen point to a given cell's local coordinates. */
  screenToCellPoint(cx: number, cy: number, cellId: string, p: Vec2): Vec2 {
    return apply(this.screenToCell(cx, cy, cellId), p);
  }

  /**
   * Zoom by `factor` keeping the screen point `anchor` fixed. Factors are
   * applied in focus-cell space, then scale is normalized by
   * descending/ascending the tree (a pure change of representation that
   * preserves world positions). Direction matters: zooming in descends when
   * scale >= 2, zooming out ascends when scale < 0.5 — at the root a scale
   * above 2 is legitimate (the whole board on screen) and must not descend.
   */
  zoomAt(cx: number, cy: number, anchor: Vec2, factor: number): void {
    const before = this.screenToFocus(cx, cy, anchor);
    this.scale *= factor;
    // Keep `anchor`'s world position fixed within the current frame...
    const afterScaleOnly = this.screenToFocus(cx, cy, anchor);
    this.x += before.x - afterScaleOnly.x;
    this.y += before.y - afterScaleOnly.y;
    // ...then normalize the representation (frame change preserves it).
    this.normalize(factor > 1 ? "in" : "out");
  }

  /** Pan by a screen-space delta. */
  panBy(dx: number, dy: number): void {
    if (this.rotation === 0) {
      this.x -= dx / this.scale;
      this.y -= dy / this.scale;
      return;
    }
    const cos = Math.cos(-this.rotation);
    const sin = Math.sin(-this.rotation);
    const rx = (dx * cos - dy * sin) / this.scale;
    const ry = (dx * sin + dy * cos) / this.scale;
    this.x -= rx;
    this.y -= ry;
  }

  /** Rotate the view around the screen point `anchor`. */
  rotateAt(cx: number, cy: number, anchor: Vec2, deltaRad: number): void {
    const before = this.screenToFocus(cx, cy, anchor);
    this.rotation += deltaRad;
    const after = this.screenToFocus(cx, cy, anchor);
    this.x += before.x - after.x;
    this.y += before.y - after.y;
  }

  /** Keep scale within [0.5, 2) relative to the focus cell. With a
   *  direction, only the matching transition is applied (see zoomAt). */
  normalize(dir?: "in" | "out"): void {
    if (dir !== "out") {
      while (this.scale >= MAX_LOCAL_SCALE && this.depth < MAX_DEPTH) {
        this.descend();
      }
    }
    if (dir !== "in") {
      while (this.scale < MIN_LOCAL_SCALE && this.depth > 0) {
        this.ascend();
      }
    }
  }

  /** Descend into the child cell containing the focus point. */
  descend(): void {
    const { cell, point } = this.tree.descend(this.tree.get(this.focusCellId), {
      x: this.x,
      y: this.y,
    });
    this.focusCellId = cell.id;
    this.x = point.x;
    this.y = point.y;
    // Child units are half the parent's: same view = half the px/unit.
    this.scale *= 0.5;
  }

  ascend(): void {
    const cell = this.tree.get(this.focusCellId);
    const parent = this.tree.parentOf(cell);
    if (!parent) return;
    const m = cell.localToParent;
    const px = this.x;
    const py = this.y;
    this.x = m.a * px + m.c * py + m.e;
    this.y = m.b * px + m.d * py + m.f;
    this.focusCellId = parent.id;
    this.scale *= 2;
  }

  /** Jump to a bookmark (cell + local point), preserving physical zoom. */
  jumpTo(cellId: string, x: number, y: number, targetScale?: number): void {
    // Ensure the target cell path exists (bookmarks restored from disk may
    // reference cells that were never recreated).
    this.tree.ensure(cellId);
    // targetScale is px/unit in the *current* frame; convert to the target
    // frame: each level of depth halves units, so scale doubles per level up.
    const depthDelta = this.focusCellId.length - cellId.length;
    this.focusCellId = cellId;
    this.x = x;
    this.y = y;
    if (targetScale !== undefined) {
      this.scale = targetScale * Math.pow(2, depthDelta);
    }
    this.normalize();
  }
}
